import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Underline } from "@tiptap/extension-underline";
import { TextAlign } from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { Highlight } from "@tiptap/extension-highlight";
import { Link } from "@tiptap/extension-link";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableCell } from "@tiptap/extension-table-cell";
import { TaskList } from "@tiptap/extension-task-list";
import { TaskItem } from "@tiptap/extension-task-item";
import { Image } from "@tiptap/extension-image";
import {
    Bold, Italic, UnderlineIcon, Strikethrough,
    Heading1, Heading2, Heading3,
    List, ListOrdered, ListChecks,
    AlignLeft, AlignCenter, AlignRight, AlignJustify,
    Quote, Minus, Undo2, Redo2, Highlighter, Link as LinkIcon,
    Table as TableIcon, Plus, Trash2,
    Image as ImageIcon, WrapText, Square, ArrowDownToLine, ArrowUpToLine,
} from "lucide-react";

// Image extension that retains custom attributes for Word-like positioning.
const PositionedImage = Image.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            "data-wrap": {
                default: "inline",
                parseHTML: (el) => el.getAttribute("data-wrap") || "inline",
                renderHTML: (attrs) => ({ "data-wrap": attrs["data-wrap"] || "inline" }),
            },
            width: {
                default: null,
                parseHTML: (el) => el.getAttribute("width"),
                renderHTML: (attrs) => attrs.width ? { width: attrs.width } : {},
            },
            style: {
                default: null,
                parseHTML: (el) => el.getAttribute("style"),
                renderHTML: (attrs) => attrs.style ? { style: attrs.style } : {},
            },
        };
    },
});

const FONTS = [
    "Inter", "Arial", "Georgia", "Times New Roman",
    "Courier New", "Calibri", "Verdana", "DM Serif Display",
];

const TEXT_COLORS = ["#131022", "#6B4EFF", "#9F1239", "#166534", "#1E40AF", "#A16207", "#374151", "#DC2626"];
const HL_COLORS  = ["#FEF3C7", "#FBCFE8", "#DDD6FE", "#BAE6FD", "#BBF7D0", "#FED7AA"];

const Sep = () => <span className="w-px h-5 bg-neutral-200 mx-0.5" />;

function ToolbarBtn({ onClick, active, title, children, testId, disabled }) {
    return (
        <button
            type="button"
            onClick={onClick}
            title={title}
            disabled={disabled}
            data-testid={testId}
            aria-pressed={active}
            className={`h-7 w-7 inline-flex items-center justify-center rounded-md transition disabled:opacity-40 disabled:cursor-not-allowed
                ${active ? "bg-brand-100 text-brand-900 ring-1 ring-brand-300" : "text-neutral-600 hover:bg-neutral-100"}`}
        >
            {children}
        </button>
    );
}

/**
 * Premium rich-text editor for Notes. TipTap powered.
 * Saves: html (full markup), text (plain extraction for previews).
 */
export default function NoteRichEditor({ value, onChange, readOnly = false, lang = "de", fontFamily = "Inter" }) {
    const editor = useEditor({
        editable: !readOnly,
        extensions: [
            StarterKit.configure({
                heading: { levels: [1, 2, 3] },
                bulletList: { keepMarks: true },
                orderedList: { keepMarks: true },
            }),
            Underline,
            TextStyle,
            Color,
            Highlight.configure({ multicolor: true }),
            Link.configure({ openOnClick: false, autolink: true }),
            TextAlign.configure({ types: ["heading", "paragraph"] }),
            Table.configure({ resizable: true }),
            TableRow,
            TableHeader,
            TableCell,
            TaskList,
            TaskItem.configure({ nested: true }),
            PositionedImage.configure({ inline: false, allowBase64: true }),
        ],
        content: value || "",
        onUpdate: ({ editor: ed }) => {
            const html = ed.getHTML();
            const text = ed.getText();
            onChange?.({ html, text });
        },
    });

    // Sync value when switching notes
    useEffect(() => {
        if (!editor) return;
        const current = editor.getHTML();
        if (value !== current) {
            editor.commands.setContent(value || "", false);
        }
    }, [value, editor]);

    // Apply font family via inline style on the container
    useEffect(() => {
        if (!editor) return;
        const root = editor.view?.dom;
        if (root) root.style.fontFamily = `'${fontFamily}', sans-serif`;
    }, [editor, fontFamily]);

    // Drop/paste images directly into the editor.
    useEffect(() => {
        if (!editor || readOnly) return;
        const dom = editor.view?.dom;
        if (!dom) return;
        const insertFile = (file) => {
            if (!file || !file.type?.startsWith("image/")) return false;
            const reader = new FileReader();
            reader.onload = () => {
                const src = String(reader.result);
                editor.chain().focus().setImage({ src }).run();
            };
            reader.readAsDataURL(file);
            return true;
        };
        const onDrop = (e) => {
            const files = Array.from(e.dataTransfer?.files || []);
            const handled = files.some(insertFile);
            if (handled) e.preventDefault();
        };
        const onPaste = (e) => {
            const items = Array.from(e.clipboardData?.items || []);
            for (const it of items) {
                if (it.kind === "file") {
                    const f = it.getAsFile();
                    if (f && insertFile(f)) { e.preventDefault(); break; }
                }
            }
        };
        dom.addEventListener("drop", onDrop);
        dom.addEventListener("paste", onPaste);
        return () => {
            dom.removeEventListener("drop", onDrop);
            dom.removeEventListener("paste", onPaste);
        };
    }, [editor, readOnly]);

    const fileInputRef = React.useRef(null);

    if (!editor) return null;

    const T = (k) => {
        const d = {
            bold: lang === "de" ? "Fett" : "Bold",
            italic: lang === "de" ? "Kursiv" : "Italic",
            underline: lang === "de" ? "Unterstreichen" : "Underline",
            strike: lang === "de" ? "Durchgestrichen" : "Strikethrough",
            h1: "Überschrift 1", h2: "Überschrift 2", h3: "Überschrift 3",
            bulList: lang === "de" ? "Aufzählung" : "Bulleted list",
            ordList: lang === "de" ? "Nummerierung" : "Numbered list",
            taskList: lang === "de" ? "Aufgabenliste" : "Task list",
            alignL: lang === "de" ? "Links" : "Left",
            alignC: lang === "de" ? "Mittig" : "Center",
            alignR: lang === "de" ? "Rechts" : "Right",
            alignJ: lang === "de" ? "Blocksatz" : "Justify",
            quote: lang === "de" ? "Zitat" : "Quote",
            hr: lang === "de" ? "Trennlinie" : "Divider",
            undo: lang === "de" ? "Rückgängig" : "Undo",
            redo: lang === "de" ? "Wiederherstellen" : "Redo",
            link: lang === "de" ? "Link" : "Link",
            highlight: lang === "de" ? "Markieren" : "Highlight",
            color: lang === "de" ? "Schriftfarbe" : "Text color",
            font: lang === "de" ? "Schriftart" : "Font",
            insTable: lang === "de" ? "Tabelle einfügen (3×3)" : "Insert table (3×3)",
            addRow: lang === "de" ? "Zeile hinzufügen" : "Add row",
            addCol: lang === "de" ? "Spalte hinzufügen" : "Add column",
            delTable: lang === "de" ? "Tabelle löschen" : "Delete table",
        };
        return d[k];
    };

    // eslint-disable-next-line react/no-unstable-nested-components
    const Btn = (p) => <ToolbarBtn {...p} disabled={p.disabled || readOnly} />;

    const insertLink = () => {
        const url = window.prompt(lang === "de" ? "URL eingeben" : "Enter URL");
        if (!url) return;
        editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    };

    const insertImageFromFile = (file) => {
        if (!file?.type?.startsWith("image/")) return;
        const reader = new FileReader();
        reader.onload = () => {
            editor.chain().focus().setImage({ src: String(reader.result) }).run();
        };
        reader.readAsDataURL(file);
    };
    const setImageWrap = (mode) => {
        // mode: inline | left | right | behind | front
        // Update the currently selected image, or the most recent one.
        const { state } = editor;
        const sel = state.selection;
        let pos = null;
        // Find an image node at/near the selection.
        state.doc.nodesBetween(sel.from, sel.to, (node, p) => {
            if (node.type.name === "image") { pos = p; return false; }
            return true;
        });
        if (pos == null) {
            // Fallback: find the last image in the doc.
            state.doc.descendants((node, p) => {
                if (node.type.name === "image") { pos = p; }
                return true;
            });
        }
        if (pos == null) return;
        const tr = state.tr.setNodeMarkup(pos, undefined, { ...state.doc.nodeAt(pos).attrs, "data-wrap": mode });
        editor.view.dispatch(tr);
    };

    return (
        <div className="flex flex-col h-full">
            {/* Toolbar */}
            {!readOnly && (
                <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-neutral-200 px-2 py-1.5 flex flex-wrap items-center gap-0.5" data-testid="note-toolbar">
                    {/* Undo/Redo */}
                    <Btn onClick={() => editor.chain().focus().undo().run()} title={T("undo")} testId="note-undo" disabled={!editor.can().undo()}><Undo2 className="w-3.5 h-3.5" /></Btn>
                    <Btn onClick={() => editor.chain().focus().redo().run()} title={T("redo")} testId="note-redo" disabled={!editor.can().redo()}><Redo2 className="w-3.5 h-3.5" /></Btn>
                    <Sep />
                    {/* Font family */}
                    <select
                        onChange={(e) => {
                            const root = editor.view?.dom;
                            if (root) root.style.fontFamily = `'${e.target.value}', sans-serif`;
                        }}
                        defaultValue={fontFamily}
                        className="h-7 text-xs border border-neutral-200 rounded px-1.5 bg-white"
                        title={T("font")}
                        data-testid="note-font"
                    >
                        {FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
                    </select>
                    <Sep />
                    {/* Headings */}
                    <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive("heading", { level: 1 })} title={T("h1")} testId="note-h1"><Heading1 className="w-3.5 h-3.5" /></Btn>
                    <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title={T("h2")} testId="note-h2"><Heading2 className="w-3.5 h-3.5" /></Btn>
                    <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} title={T("h3")} testId="note-h3"><Heading3 className="w-3.5 h-3.5" /></Btn>
                    <Sep />
                    {/* Marks */}
                    <Btn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title={T("bold")} testId="note-bold"><Bold className="w-3.5 h-3.5" /></Btn>
                    <Btn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title={T("italic")} testId="note-italic"><Italic className="w-3.5 h-3.5" /></Btn>
                    <Btn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} title={T("underline")} testId="note-underline"><UnderlineIcon className="w-3.5 h-3.5" /></Btn>
                    <Btn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} title={T("strike")} testId="note-strike"><Strikethrough className="w-3.5 h-3.5" /></Btn>
                    {/* Text color */}
                    <div className="relative group">
                        <Btn onClick={() => editor.chain().focus().unsetColor().run()} title={T("color")} testId="note-color-pop">
                            <span className="w-3 h-3 rounded-sm border border-neutral-300" style={{ background: editor.getAttributes("textStyle").color || "#131022" }} />
                        </Btn>
                        <div className="hidden group-hover:flex absolute top-7 left-0 z-20 bg-white border border-neutral-200 rounded-md shadow-lg p-1 gap-1">
                            {TEXT_COLORS.map((c) => (
                                <button
                                    key={c}
                                    onClick={() => editor.chain().focus().setColor(c).run()}
                                    className="w-4 h-4 rounded-sm border border-neutral-200 hover:scale-110 transition"
                                    style={{ background: c }}
                                    data-testid={`note-color-${c}`}
                                    type="button"
                                />
                            ))}
                        </div>
                    </div>
                    {/* Highlight */}
                    <div className="relative group">
                        <Btn onClick={() => editor.chain().focus().unsetHighlight().run()} title={T("highlight")} testId="note-highlight-pop" active={editor.isActive("highlight")}>
                            <Highlighter className="w-3.5 h-3.5" />
                        </Btn>
                        <div className="hidden group-hover:flex absolute top-7 left-0 z-20 bg-white border border-neutral-200 rounded-md shadow-lg p-1 gap-1">
                            {HL_COLORS.map((c) => (
                                <button
                                    key={c}
                                    onClick={() => editor.chain().focus().toggleHighlight({ color: c }).run()}
                                    className="w-4 h-4 rounded-sm border border-neutral-200 hover:scale-110 transition"
                                    style={{ background: c }}
                                    type="button"
                                />
                            ))}
                        </div>
                    </div>
                    <Sep />
                    {/* Alignment */}
                    <Btn onClick={() => editor.chain().focus().setTextAlign("left").run()} active={editor.isActive({ textAlign: "left" })} title={T("alignL")} testId="note-align-left"><AlignLeft className="w-3.5 h-3.5" /></Btn>
                    <Btn onClick={() => editor.chain().focus().setTextAlign("center").run()} active={editor.isActive({ textAlign: "center" })} title={T("alignC")} testId="note-align-center"><AlignCenter className="w-3.5 h-3.5" /></Btn>
                    <Btn onClick={() => editor.chain().focus().setTextAlign("right").run()} active={editor.isActive({ textAlign: "right" })} title={T("alignR")} testId="note-align-right"><AlignRight className="w-3.5 h-3.5" /></Btn>
                    <Btn onClick={() => editor.chain().focus().setTextAlign("justify").run()} active={editor.isActive({ textAlign: "justify" })} title={T("alignJ")} testId="note-align-justify"><AlignJustify className="w-3.5 h-3.5" /></Btn>
                    <Sep />
                    {/* Lists */}
                    <Btn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title={T("bulList")} testId="note-ul"><List className="w-3.5 h-3.5" /></Btn>
                    <Btn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title={T("ordList")} testId="note-ol"><ListOrdered className="w-3.5 h-3.5" /></Btn>
                    <Btn onClick={() => editor.chain().focus().toggleTaskList().run()} active={editor.isActive("taskList")} title={T("taskList")} testId="note-task"><ListChecks className="w-3.5 h-3.5" /></Btn>
                    <Sep />
                    {/* Quote / HR / Link */}
                    <Btn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} title={T("quote")} testId="note-quote"><Quote className="w-3.5 h-3.5" /></Btn>
                    <Btn onClick={() => editor.chain().focus().setHorizontalRule().run()} title={T("hr")} testId="note-hr"><Minus className="w-3.5 h-3.5" /></Btn>
                    <Btn onClick={insertLink} active={editor.isActive("link")} title={T("link")} testId="note-link"><LinkIcon className="w-3.5 h-3.5" /></Btn>
                    <Sep />
                    {/* Tables */}
                    <Btn onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} title={T("insTable")} testId="note-tbl-insert"><TableIcon className="w-3.5 h-3.5" /></Btn>
                    {editor.isActive("table") && (
                        <>
                            <Btn onClick={() => editor.chain().focus().addRowAfter().run()} title={T("addRow")} testId="note-tbl-add-row"><Plus className="w-3.5 h-3.5" /></Btn>
                            <Btn onClick={() => editor.chain().focus().addColumnAfter().run()} title={T("addCol")} testId="note-tbl-add-col">
                                <Plus className="w-3.5 h-3.5 rotate-90" />
                            </Btn>
                            <Btn onClick={() => editor.chain().focus().deleteTable().run()} title={T("delTable")} testId="note-tbl-del"><Trash2 className="w-3.5 h-3.5 text-red-500" /></Btn>
                        </>
                    )}
                    <Sep />
                    {/* Images */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) insertImageFromFile(f);
                            e.target.value = "";
                        }}
                        data-testid="note-image-file"
                    />
                    <Btn onClick={() => fileInputRef.current?.click()} title={lang === "de" ? "Bild einfügen" : "Insert image"} testId="note-image-insert"><ImageIcon className="w-3.5 h-3.5" /></Btn>
                    <Btn onClick={() => setImageWrap("inline")} title={lang === "de" ? "Bild: Inline (mit Text)" : "Inline with text"} testId="note-img-inline"><Square className="w-3.5 h-3.5" /></Btn>
                    <Btn onClick={() => setImageWrap("left")} title={lang === "de" ? "Bild: Umfluss links (Eng links)" : "Wrap left"} testId="note-img-wrap-left"><WrapText className="w-3.5 h-3.5 -scale-x-100" /></Btn>
                    <Btn onClick={() => setImageWrap("right")} title={lang === "de" ? "Bild: Umfluss rechts (Eng rechts)" : "Wrap right"} testId="note-img-wrap-right"><WrapText className="w-3.5 h-3.5" /></Btn>
                    <Btn onClick={() => setImageWrap("behind")} title={lang === "de" ? "Bild: Hinter den Text" : "Behind text"} testId="note-img-behind"><ArrowDownToLine className="w-3.5 h-3.5" /></Btn>
                    <Btn onClick={() => setImageWrap("front")} title={lang === "de" ? "Bild: Vor den Text" : "In front of text"} testId="note-img-front"><ArrowUpToLine className="w-3.5 h-3.5" /></Btn>
                </div>
            )}
            <div className="flex-1 overflow-y-auto">
                <EditorContent editor={editor} className="note-tiptap" />
            </div>
        </div>
    );
}
